/* eslint-disable react/jsx-props-no-spreading */
import type { ComponentType, CSSProperties, RefObject } from 'react';
import React, { useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import { usePopper } from 'react-popper';
import type { Options, Placement } from '@popperjs/core';

export enum WithPositionPlacement {
  TOP = 'top',
  TOP_START = 'top-start',
  TOP_END = 'top-end',
  BOTTOM = 'bottom',
  BOTTOM_START = 'bottom-start',
  BOTTOM_END = 'bottom-end',
  RIGHT = 'right',
  RIGHT_START = 'right-start',
  RIGHT_END = 'right-end',
  LEFT = 'left',
  LEFT_START = 'left-start',
  LEFT_END = 'left-end',
  AUTO = 'auto',
}

export interface WithPositionProps {
  /**
   * Reference component for positioning the list. Needed by [Popper.js](https://popper.js.org/react-popper/v2/hook/)
   */
  referenceElement: RefObject<Element> | null;
  /**
   * Relative position of the list. Needed by [Popper.js](https://popper.js.org/docs/v2/utils/detect-overflow/#placement)
   */
  placement: Placement;
  /**
   * Relative position of the list to its reference when it cannot be fully rendered on the screen.
   * Used by [Popper.js](https://popper.js.org/docs/v2/modifiers/flip/#fallbackplacements)
   */
  fallbackPlacements?: Placement[];
  /**
   * Separation from the reference component. Used by [Popper.js](https://popper.js.org/docs/v2/modifiers/offset/#skidding-1)
   */
  skidding?: number;
  /**
   *  Distance from the reference component
   */
  distance?: number;
  width?: string;
  /**
   * Area that the element will be checked for overflow relative to. Used by [Popper.js](https://popper.js.org/docs/v2/utils/detect-overflow/#boundary)
   */
  boundary?: string | Element;
  /**
   * Define where inject the component
   */
  renderReferencePortalNode?: HTMLElement;
  /**
   * Custom styles
   */
  style?: CSSProperties;
  /*
   * positioning strategy to use. Used by [Popper.js](https://popper.js.org/react-popper/v2/render-props/#strategy)
   */
  strategy?: 'absolute' | 'fixed';
  /**
   * Set list displacement from its reference element
   */
  offset?: (options: Options) => [number | null | undefined, number | null | undefined];
  /**
   * Indicates whether the list positioning logic should listen to the scroll event. By default it always listens to it
   * Used by [Popper.js](https://popper.js.org/docs/v2/modifiers/event-listeners/#scroll)
   */
  scrollListener?: boolean;
  /**
   * Children elements reference
   */
  forwardedRef?: RefObject<HTMLElement>;
}

function withPosition<T>(Component: ComponentType<T>) {
  return (hocProps: T & WithPositionProps) => {
    const {
      placement,
      fallbackPlacements = [WithPositionPlacement.AUTO],
      referenceElement: refElement,
      renderReferencePortalNode,
      skidding = 0,
      distance = 0,
      boundary = 'clippingParents',
      style = {},
      offset,
      strategy = 'absolute',
      width,
      scrollListener = true,
      forwardedRef,
    } = hocProps;

    const [referenceElement, setReferenceElement] = useState<Element | null>(null);
    const [popoverElement, setPopoverElement] = useState<HTMLDivElement | null>(null);

    useEffect(() => {
      if (refElement) {
        setReferenceElement(refElement.current);
      }
    }, [refElement]);

    const offsetModifier = useMemo(
      () => ({
        name: 'offset',
        options: {
          offset: (offsetData: Options) => {
            if (offset) {
              return offset(offsetData);
            }

            return [skidding, distance];
          },
        },
      }),
      [offset, skidding, distance],
    );

    const modifiers = useMemo(
      () => [
        {
          name: 'flip',
          options: {
            fallbackPlacements,
            boundary,
          },
        },
        {
          name: 'eventListeners',
          options: {
            scroll: scrollListener,
          },
        },
        { ...offsetModifier },
      ],
      [boundary, fallbackPlacements, offsetModifier, scrollListener],
    );

    const { styles, attributes, state, update } = usePopper(
      referenceElement,
      popoverElement,
      {
        strategy,
        modifiers,
        placement,
      },
    );

    const PopoverNode = (
      <div
        ref={setPopoverElement}
        style={{
          ...styles.popper,
          width,
          zIndex: 3,
          ...style,
        }}
        {...attributes.popper}
      >
        <Component
          {...hocProps}
          position={state?.placement}
          update={update}
          ref={forwardedRef}
        />
      </div>
    );

    return renderReferencePortalNode
      ? ReactDOM.createPortal(PopoverNode, renderReferencePortalNode)
      : PopoverNode;
  };
}

withPosition.Placement = WithPositionPlacement;

export default withPosition;
